import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  Clipboard,
  Copy,
  QrCode,
  Share2,
  ShieldCheck,
} from "lucide-react";

import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { useAppSelector } from "@/store/hooks";
import type { WalletAsset } from "@/interfaces/asset";
import type { Wallet } from "@/interfaces/wallet";
import { normalizeCurrency, removeTrailingToken } from "@/utils";
import { useSystemSettings } from "@/hooks";
import Loader from "@/components/loader/Loader";
import { ErrorState } from "@/components/ErrorState";

interface Props {
  wallet: Wallet | undefined;
  assets: WalletAsset[] | undefined;
}

const DepositContainer = ({ wallet: _wallet, assets }: Props) => {
  const { theme } = useAppSelector((state) => state.ui);

  const [selectedAssetId, setSelectedAssetId] = useState<string | undefined>(
    assets?.[0]?.id,
  );

  const [showAssetDropdown, setShowAssetDropdown] = useState(false);

  const [copiedField, setCopiedField] = useState<"address" | "memo" | null>(
    null,
  );

  const {
    data: adminSettings,
    isLoading,
    isError,
    refetch,
  } = useSystemSettings();

  const minimumDeposit = adminSettings?.depositMinUSD ?? 5;

  const selectedAsset = useMemo(() => {
    if (!assets?.length) {
      return undefined;
    }

    return assets.find((asset) => asset.id === selectedAssetId) ?? assets[0];
  }, [assets, selectedAssetId]);

  const activeNetwork = selectedAsset?.token?.blockchainNetwork;

  const depositAddress = selectedAsset?.walletAddress?.address;

  const depositMemo = undefined;

  const assetName = removeTrailingToken(selectedAsset?.token?.name ?? "");

  const assetSymbol = normalizeCurrency(
    selectedAsset?.currency ?? selectedAsset?.token?.symbol ?? "",
  );

  const assetIcon = useMemo(() => {
    if (!selectedAsset) {
      return undefined;
    }

    if (selectedAsset.iso2) {
      return `https://flagcdn.com/w80/${selectedAsset.iso2.toLowerCase()}.png`;
    }

    return selectedAsset.token?.icon ?? undefined;
  }, [selectedAsset]);

  useEffect(() => {
    if (!assets?.length) {
      setSelectedAssetId(undefined);
      return;
    }

    const stillExists = assets.some((asset) => asset.id === selectedAssetId);

    if (!stillExists) {
      setSelectedAssetId(assets[0].id);
    }
  }, [assets, selectedAssetId]);

  const copyToClipboard = async (value: string, field: "address" | "memo") => {
    try {
      await navigator.clipboard.writeText(value);

      setCopiedField(field);

      toast.success(
        field === "address" ? "Deposit address copied." : "Memo copied.",
      );

      window.setTimeout(() => {
        setCopiedField(null);
      }, 2000);
    } catch {
      toast.error("Unable to copy.");
    }
  };

  const shareAddress = async () => {
    if (!depositAddress) {
      return;
    }

    const shareData = {
      title: `Deposit ${assetSymbol}`,
      text: depositAddress,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await copyToClipboard(depositAddress, "address");
      }
    } catch {
      // User cancelled sharing.
    }
  };

  const handleAssetChange = (asset: WalletAsset) => {
    setSelectedAssetId(asset.id);

    setShowAssetDropdown(false);

    setCopiedField(null);
  };

  if (!assets?.length) {
    return (
      <div className="w-full">
        <div
          className={`mx-auto w-full max-w-4xl rounded-xl ${
            theme === "dark" ? "bg-card" : "bg-white"
          }`}
        >
          <div className="p-2 sm:p-4">
            <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/10 p-2 sm:p-4">
              <div className="flex gap-3">
                <AlertTriangle className="h-5 w-5 shrink-0 text-yellow-500" />

                <div>
                  <p className="font-medium text-yellow-500">
                    No deposit assets available
                  </p>

                  <p className="mt-1 text-yellow-500/80">
                    This wallet currently has no assets enabled for deposits.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-lenis-prevent className="w-full overflow-y-auto">
      <div
        className={`mx-auto w-full max-w-4xl overflow-y-auto rounded-xl ${
          theme === "dark" ? "bg-card" : "bg-white"
        }`}
      >
        <div
          className={`flex items-center justify-between border-b p-2 sm:p-4 ${
            theme === "dark" ? "border-border" : "border-gray-200"
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="relative h-11 w-11 shrink-0">
              {assetIcon ? (
                <img
                  src={assetIcon}
                  alt={selectedAsset?.token?.name ?? assetSymbol}
                  className="h-11 w-11 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10">
                  <QrCode className="h-5 w-5 text-primary" />
                </div>
              )}

              {/* Network icon only for crypto assets */}
              {!selectedAsset?.iso2 && activeNetwork?.icon && (
                <img
                  src={activeNetwork.icon}
                  alt={activeNetwork.name}
                  className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full border-2 border-background bg-background object-cover"
                />
              )}
            </div>

            <div>
              <h3 className="text-lg font-semibold">
                Deposit {assetName || assetSymbol} ({activeNetwork?.name})
              </h3>

              <p className="text-muted-foreground">
                Receive {assetSymbol} securely
                {activeNetwork?.name ? ` on ${activeNetwork.name}` : ""}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-5 mt-4 p-2 sm:p-4">
          <div>
            <label className="mb-2 block font-medium">Asset</label>

            <div className="relative">
              <button
                type="button"
                onClick={() => setShowAssetDropdown((value) => !value)}
                className={`flex w-full items-center rounded-xl border p-3 text-left transition cursor-pointer ${
                  theme === "dark"
                    ? "border-border bg-muted hover:bg-muted/80"
                    : "border-gray-200 bg-gray-50 hover:bg-gray-100"
                }`}
              >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <div className="relative h-10 w-10 shrink-0">
                    {assetIcon ? (
                      <img
                        src={assetIcon}
                        alt={selectedAsset?.token?.name ?? assetSymbol}
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                        <QrCode className="h-4 w-4 text-primary" />
                      </div>
                    )}

                    {!selectedAsset?.iso2 && activeNetwork?.icon && (
                      <img
                        src={activeNetwork.icon}
                        alt={activeNetwork.name}
                        className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full border-2 border-background bg-background object-cover"
                      />
                    )}
                  </div>

                  <div className="min-w-0">
                    <p className="font-medium">{assetSymbol}</p>

                    <p className="truncate text-muted-foreground">
                      {assetName}
                      {activeNetwork?.name ? ` • ${activeNetwork.name}` : ""}
                    </p>
                  </div>
                </div>

                {/* Select indicator */}
                <div className="ml-3 flex h-7 shrink-0 items-center gap-2">
                  <div
                    className={`h-6 w-px ${
                      theme === "dark" ? "bg-border" : "bg-gray-200"
                    }`}
                  />

                  <div className="flex items-center gap-1.5 pl-2 text-sm font-semibold text-primary">
                    <span>Select</span>

                    <ChevronDown
                      className={`h-[18px] w-[18px] transition-transform duration-200 ${
                        showAssetDropdown ? "rotate-180" : "rotate-0"
                      }`}
                      strokeWidth={2.5}
                    />
                  </div>
                </div>
              </button>

              {showAssetDropdown && (
                <div
                  data-lenis-prevent
                  className={`absolute left-0 right-0 top-full z-30 mt-2 max-h-80 overflow-y-auto rounded-xl border shadow-xl ${
                    theme === "dark"
                      ? "border-border bg-card"
                      : "border-gray-200 bg-white"
                  }`}
                >
                  {assets
                    .filter(
                      (asset) =>
                        asset.isActive &&
                        asset.token?.blockchainNetwork?.depositEnabled,
                    )
                    .map((asset) => {
                      const network = asset.token?.blockchainNetwork;

                      const symbol = normalizeCurrency(
                        asset.currency ?? asset.token?.symbol ?? "",
                      );

                      const name = removeTrailingToken(asset.token?.name ?? "");

                      /*
                       * Main asset icon:
                       *
                       * Fiat -> country flag
                       * Crypto -> token icon
                       */
                      const icon = asset.iso2
                        ? `https://flagcdn.com/w80/${asset.iso2.toLowerCase()}.png`
                        : (asset.token?.icon ?? undefined);

                      return (
                        <button
                          type="button"
                          key={asset.id}
                          onClick={() => handleAssetChange(asset)}
                          className={`flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left transition ${
                            selectedAsset?.id === asset.id
                              ? "bg-primary/5"
                              : "hover:bg-muted"
                          }`}
                        >
                          <div className="relative h-10 w-10 shrink-0">
                            {icon ? (
                              <img
                                src={icon}
                                alt={name || symbol}
                                className="h-10 w-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                                <QrCode className="h-4 w-4 text-primary" />
                              </div>
                            )}

                            {/* Network badge */}
                            {!asset.iso2 && network?.icon && (
                              <img
                                src={network.icon}
                                alt={network.name}
                                className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full border-2 border-background bg-background object-cover"
                              />
                            )}
                          </div>

                          <div className="min-w-0 flex-1">
                            <p className="font-medium">{symbol}</p>

                            <p className="truncate text-[12px] text-muted-foreground">
                              {name}
                              {network?.name ? ` • ${network.name}` : ""}
                            </p>
                          </div>

                          {selectedAsset?.id === asset.id && (
                            <Check className="h-[18px] w-[18px] shrink-0 text-primary" />
                          )}
                        </button>
                      );
                    })}
                </div>
              )}
            </div>
          </div>

          {activeNetwork ? (
            <div>
              <label className="mb-2 block font-medium">Network</label>

              <div
                className={`flex items-center gap-3 rounded-xl border p-3 ${
                  theme === "dark"
                    ? "border-border bg-muted"
                    : "border-gray-200 bg-gray-50"
                }`}
              >
                {activeNetwork.icon ? (
                  <img
                    src={activeNetwork.icon}
                    alt={activeNetwork.name}
                    className="h-8 w-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                    <QrCode className="h-4 w-4 text-primary" />
                  </div>
                )}

                <div>
                  <p className="font-medium">{activeNetwork.name}</p>

                  <p className="text text-muted-foreground">
                    {activeNetwork.code}
                    {activeNetwork.chainId
                      ? ` • Chain ID ${activeNetwork.chainId}`
                      : ""}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/10 p-4">
              <div className="flex gap-3">
                <AlertTriangle className="h-5 w-5 shrink-0 text-yellow-500" />

                <div>
                  <p className="font-medium text-yellow-500">
                    Network unavailable
                  </p>

                  <p className="mt-1 text-yellow-500/80">
                    This asset does not have a valid blockchain network
                    configured.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeNetwork && !depositAddress && (
            <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-4">
              <div className="flex gap-3">
                <AlertTriangle className="h-5 w-5 shrink-0 text-red-500" />

                <div>
                  <p className="font-medium text-red-500">
                    Deposit address unavailable
                  </p>

                  <p className="mt-1 text-red-500/80">
                    No wallet address is associated with this asset.
                  </p>
                </div>
              </div>
            </div>
          )}

          {depositAddress && activeNetwork && (
            <>
              <div className="rounded-2xl border border-border bg-muted/30 p-5">
                <div className="mb-4 flex items-center justify-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-emerald-500" />

                  <span className="font-medium">
                    Your {assetSymbol} deposit address
                  </span>
                </div>

                <div className="flex justify-center">
                  <div className="rounded-2xl bg-white p-4 shadow-sm">
                    <QRCodeSVG value={depositAddress} size={190} level="H" />
                  </div>
                </div>

                <p className="mt-4 text-center text-muted-foreground">
                  Scan this QR code with your wallet
                </p>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="font-medium">Deposit Address</label>

                  <button
                    type="button"
                    onClick={shareAddress}
                    className="flex items-center gap-1 text-primary"
                  >
                    <Share2 className="h-3.5 w-3.5" />
                    Share
                  </button>
                </div>

                <div
                  className={`flex items-center gap-2 rounded-xl border p-3 ${
                    theme === "dark"
                      ? "border-border bg-muted"
                      : "border-gray-200 bg-gray-50"
                  }`}
                >
                  <code className="min-w-0 flex-1 break-all font-mono sm:text-sm">
                    {depositAddress}
                  </code>

                  <button
                    type="button"
                    onClick={() => copyToClipboard(depositAddress, "address")}
                    className="shrink-0 rounded-lg p-2 text-primary transition hover:bg-primary/10"
                  >
                    {copiedField === "address" ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <div
                className={`mt-3 rounded-xl border p-3.5 ${
                  theme === "dark"
                    ? "border-border/60 bg-muted/40"
                    : "border-gray-200/80 bg-gray-50/80"
                }`}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Minimum deposit
                    </p>

                    <p className="mt-1 text-sm font-semibold text-foreground">
                      {minimumDeposit} USDT equivalent
                    </p>
                  </div>

                  <div className="shrink-0 rounded-lg bg-primary/10 px-2.5 py-1.5">
                    <span className="text-xs font-medium text-primary">
                      {activeNetwork?.name}
                    </span>
                  </div>
                </div>

                <div
                  className={`mt-3 border-t pt-3 ${
                    theme === "dark" ? "border-border/50" : "border-gray-200/70"
                  }`}
                >
                  <p className="text-xs leading-5 text-muted-foreground">
                    Deposits are credited once your cumulative deposits reach
                    the minimum of {minimumDeposit} {assetSymbol}.
                  </p>
                </div>
              </div>

              {activeNetwork.supportsMemo && (
                <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-4">
                  <div className="mb-2 flex items-center gap-2">
                    <Clipboard className="h-4 w-4 text-yellow-500" />

                    <p className="font-semibold text-yellow-500">
                      Memo / Destination Tag Required
                    </p>
                  </div>

                  <p className="mb-3 text-yellow-500/80">
                    You must include the following memo or destination tag when
                    sending this deposit.
                  </p>

                  {depositMemo ? (
                    <div className="flex items-center gap-2 rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-3">
                      <code className="min-w-0 flex-1 break-all font-mono font-semibold text-yellow-500">
                        {depositMemo}
                      </code>

                      <button
                        type="button"
                        onClick={() => copyToClipboard(depositMemo, "memo")}
                        className="shrink-0 rounded-lg p-2 text-yellow-500 transition hover:bg-yellow-500/10"
                      >
                        {copiedField === "memo" ? (
                          <Check className="h-4 w-4" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3">
                      <p className="font-medium text-red-500">
                        Memo / destination tag unavailable.
                      </p>

                      <p className="mt-1 text-red-500/80">
                        Do not send funds until the required memo/tag is
                        provided.
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="overflow-hidden rounded-xl border border-border">
                <div className="grid grid-cols-2 divide-x divide-border">
                  <div className="p-4">
                    <p className="text-muted-foreground">Asset</p>

                    <p className="mt-1 font-semibold">{assetSymbol}</p>
                  </div>

                  <div className="p-4">
                    <p className="text-muted-foreground">Network</p>

                    <p className="mt-1 font-semibold">{activeNetwork.name}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 divide-x divide-border border-t">
                  <div className="p-4">
                    <p className="text-muted-foreground">Confirmations</p>

                    <p className="mt-1 font-semibold">
                      {activeNetwork.confirmations}
                    </p>
                  </div>

                  <div className="p-4">
                    <p className="text-xs text-muted-foreground">Standard</p>

                    <p className="mt-1 text-sm font-semibold">
                      {selectedAsset.token.standard}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />

                  <div>
                    <p className="text-sm font-semibold text-red-500">
                      Deposit only {assetSymbol}
                    </p>

                    <p className="mt-1 text-xs leading-5 text-red-500/80">
                      Send only {assetSymbol} using the {activeNetwork.name}{" "}
                      network to this address. Sending another asset or using
                      another network may result in permanent loss of funds.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl bg-emerald-500/10 p-4">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />

                  <div>
                    <p className="text-sm font-medium text-emerald-500">
                      Deposits are automatically detected
                    </p>

                    <p className="mt-1 text-xs leading-5 text-emerald-500/80">
                      Once your transaction is broadcast, our system monitors
                      the {activeNetwork.name} blockchain and credits your
                      wallet after the required confirmations.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DepositContainer;
